<template>
	<v-dialog v-model="dialog" max-width="950px" persistent>
		<v-card>
			<v-toolbar flat dense>
				<v-toolbar-title class="subheading">{{ isEdit ? 'Edit' : 'Add' }} Certification Type</v-toolbar-title>
			</v-toolbar>

			<v-card-text class="text-xs-left">
				<div v-if="isEdit" class="grey--text mt-1 mb-4">
					<v-icon small class="mr-1" color="warning">mdi-alert-circle-outline</v-icon>
					Any changes will apply to all existing certifications of this type.
				</div>

				<v-form ref="form" lazy-validation>
					<v-container grid-list-xl fluid>
						<v-layout row wrap>
							<v-flex xs12 sm6>
								<v-text-field label="Certification Name" ref="nameInput" v-model="model.name" :rules="[v => !!v || 'Name is required']" :readonly="isEdit" required />
								<v-textarea v-model="model.description" auto-grow rows="3" box label="Description" />
							</v-flex>

							<v-flex xs12 sm6>
								<v-card flat class="pa-3 grey lighten-4">
									<div class="grey--text text--darken-2 mb-2">
										<v-icon small class="mr-1">mdi-calendar-remove</v-icon>
										Expiry Settings
									</div>
									<v-switch v-model="model.configuration.hasExpiry" label="This certification expires" hint="If on, expiry dates will be required when uploading this certification." persistent-hint color="primary" />
								</v-card>

								<v-expand-transition>
									<div v-show="model.configuration.hasExpiry">
										<v-divider class="my-4"></v-divider>

										<div class="grey--text text--darken-2 mb-2">
											<v-icon small class="mr-1">mdi-account-multiple</v-icon>
											Who should be notified when this certification is due to expire?
										</div>
										<v-layout row wrap>
											<v-flex xs12 sm6>
												<v-switch label="Remind the Employee" v-model="model.configuration.remindEmployee" color="primary" />
											</v-flex>
											<v-flex xs12 sm6>
												<v-switch label="Remind the Manager" v-model="model.configuration.remindManager" color="primary" />
											</v-flex>
										</v-layout>

										<v-expand-transition>
											<div v-show="model.configuration.remindEmployee || model.configuration.remindManager">
												<v-divider class="my-4"></v-divider>

												<div class="grey--text text--darken-2 mb-2">
													<v-icon small class="mr-1">mdi-bell</v-icon>
													Reminder Schedule
												</div>
												<div class="caption grey--text mb-2">
													Reminders help employees and their managers stay on top of expiring certifications. You can customise when these reminders are sent, or leave them as the default schedule.
												</div>
												<div v-if="!configureReminders" class="mb-2">
													<div class="caption grey--text mb-1">
														<span v-if="model.configuration.renewalReminders.length">
															Reminders will be sent on these days before expiry:
														</span>
														<span v-else>No reminders are currently set.</span>
													</div>

													<v-slide-x-reverse-transition group>
														<v-chip v-for="(reminder, i) in sortRenewalReminders(model.configuration.renewalReminders)" :key="'reminder-summary-' + i" small class="mr-2 mb-1">
															{{ formatInterval(reminder.interval) }}
														</v-chip>
													</v-slide-x-reverse-transition>
												</div>

												<a href="javascript:void(0)" @click="configureReminders = !configureReminders" style="display:block;" class="mb-2">
													{{ configureReminders ? 'Hide Reminder Schedule' : 'Adjust Reminder Schedule' }}
												</a>

												<v-expand-transition>
													<div v-show="configureReminders">
														<v-slide-x-reverse-transition group>
															<v-chip
																v-for="(reminder, i) in sortRenewalReminders(model.configuration.renewalReminders)"
																:key="'renewal-' + i"
																close
																class="mr-2 mb-2"
																@input="removeReminder('renewalReminders', reminder)"
															>
																{{ formatInterval(reminder.interval) }}
															</v-chip>
														</v-slide-x-reverse-transition>

														<v-layout row wrap align-center>
															<v-flex xs6>
																<v-text-field v-model="newReminderInput" type="number" :label="`${newReminderInterval} Before Expiry`" hide-details clearable />
															</v-flex>
															<v-flex xs3>
																<v-select v-model="newReminderInterval" :items="availableIntervals" hide-details dense />
															</v-flex>
															<v-flex xs3>
																<v-btn small color="primary" :disabled="!newReminderInput" @click="addReminder('renewalReminders')">
																	+ Add
																</v-btn>
															</v-flex>
														</v-layout>
													</div>
												</v-expand-transition>
											</div>
										</v-expand-transition>
									</div>
								</v-expand-transition>
							</v-flex>
						</v-layout>
					</v-container>
				</v-form>
			</v-card-text>

			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn flat @click="close">Cancel</v-btn>
				<v-btn color="primary" flat @click="save" :disabled="isSaveBtnDisabled" :loading="isSaving">Save</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
	import { formatInterval, parseTimespan, sortRenewalReminders, stringifyInterval } from '@utils/timespanUtil';
	import { mapActions } from 'vuex';

	export default {
		name: 'CertificationTypeEditor',
		props: {
			value: Boolean,
			certificationType: Object
		},
		data() {
			return {
				dialog: false,
				model: this.getEmptyModel(),
				newReminderInput: null,
				newReminderInterval: 'Days',
				availableIntervals: ['Days', 'Weeks'],
				configureReminders: false,
				isSaving: false
			};
		},
		computed: {
			isEdit() {
				return !!this.certificationType;
			},
			isSaveBtnDisabled() {
				return JSON.stringify(this.model) === JSON.stringify(this.certificationType) || !this.model.name;
			},
			reminderSummary() {
				const reminders = this.model.configuration.renewalReminders.map(r => r.interval.days).sort((a, b) => b - a);
				if (reminders.length === 0) return 'No reminders are currently set.';

				const formatted = reminders.map(d => `<strong>${d}</strong>`).join(', ');
				const defaultReminders = [30, 14, 7, 1];

				return JSON.stringify(reminders) === JSON.stringify(defaultReminders) ? `By default, reminders are sent out ${formatted} day(s) before expiry.` : `Reminders are currently set to be sent out ${formatted} day(s) before expiry.`;
			}
		},
		watch: {
			value(val) {
				this.dialog = val;
				if (val) {
					this.model = this.certificationType ? this.cloneCertification(this.certificationType) : this.getEmptyModel();
					this.configureReminders = false;
					this.$nextTick(() => {
						this.$refs.form.resetValidation();
						this.$refs.nameInput.focus();
					});
				}
			},
			dialog(val) {
				this.$emit('input', val);
			}
		},
		methods: {
			formatInterval,
			sortRenewalReminders,
			...mapActions({
				upsertCertificationType: 'certificationTypes/upsert',
				refreshCertifications: 'certifications/getActiveEmployee'
			}),
			getEmptyModel() {
				return {
					name: '',
					description: '',
					configuration: {
						hasExpiry: true,
						remindEmployee: true,
						remindManager: true,
						renewalReminders: [
							{ interval: { days: 30 }, reminderType: 'Upload' },
							{ interval: { days: 14 }, reminderType: 'Upload' },
							{ interval: { days: 7 }, reminderType: 'Upload' },
							{ interval: { days: 1 }, reminderType: 'Upload' }
						]
					},
					isDeleted: false
				};
			},
			cloneCertification(cert) {
				const clone = JSON.parse(JSON.stringify(cert));
				clone.configuration.renewalReminders = clone.configuration.renewalReminders.map(r => ({
					interval: parseTimespan(r.interval),
					reminderType: r.reminderType
				}));
				return clone;
			},
			addReminder(type) {
				const days = Number.parseInt(this.newReminderInput);
				if (isNaN(days)) return;

				const intervalDays = this.newReminderInterval === 'Weeks' ? days * 7 : days;
				this.model.configuration[type].push({ interval: { days: intervalDays } });

				this.newReminderInput = null;
			},
			removeReminder(type, reminder) {
				const idx = this.model.configuration[type].findIndex(r => JSON.stringify(r) === JSON.stringify(reminder));
				if (idx !== -1) this.model.configuration[type].splice(idx, 1);
			},
			close() {
				this.dialog = false;
			},
			getModelToSave() {
				return {
					...this.model,
					configuration: {
						...this.model.configuration,
						renewalReminders: this.model.configuration.renewalReminders.map(r => ({
							interval: stringifyInterval(r.interval),
							reminderType: 'Upload'
						}))
					}
				};
			},
			async save() {
				if (!this.$refs.form.validate()) return;

				this.isSaving = true;
				const result = await this.upsertCertificationType(this.getModelToSave());

				if (result.isSuccess) {
					await this.refreshCertifications();
					this.close();
				}

				this.isSaving = false;
			}
		}
	};
</script>

<style scoped></style>
